import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import config from '@/config';
import fetchWrapper from '@/services/fetchWrapper.service';

// Optional: enable with REACT_APP_ENABLE_LE_COPY=true
const LE_COPY_ENABLED = process.env.REACT_APP_ENABLE_LE_COPY === 'true';

function logToSentry(error) {
  if (typeof window !== 'undefined' && window.Sentry?.captureException) {
    window.Sentry.captureException(error);
  } else {
    console.error(error);
  }
}

export default function useLECopy(productId, editionNumber, options = {}) {
  const { enabled = true, initialData = null } = options;
  const shouldLoad = Boolean(
    LE_COPY_ENABLED
    && enabled
    && productId
    && (editionNumber || editionNumber === 0),
  );

  const [state, setState] = useState(() => ({
    data: initialData,
    loading: shouldLoad && !initialData,
    error: null,
  }));

  const requestIdRef = useRef(0);

  const resetState = useCallback(() => {
    setState({
      data: initialData,
      loading: false,
      error: null,
    });
  }, [initialData]);

  const fetchCopy = useCallback(async () => {
    if (!shouldLoad) {
      resetState();
      return null;
    }

    const currentRequestId = requestIdRef.current + 1;
    requestIdRef.current = currentRequestId;

    setState((prev) => ({
      ...prev,
      loading: true,
      error: null,
    }));

    try {
      const endpoint = `${config.apis.api.url}/api/editions/${encodeURIComponent(productId)}/copies/${encodeURIComponent(editionNumber)}`;
      const { response, data } = await fetchWrapper.get(endpoint, { injectToken: true });

      if (!response?.ok) {
        throw new Error(`Failed to load edition copy (${response?.status ?? 'unknown'})`);
      }

      if (requestIdRef.current !== currentRequestId) {
        return data ?? null;
      }

      setState({
        data: data ?? null,
        loading: false,
        error: null,
      });

      return data ?? null;
    } catch (error) {
      logToSentry(error);

      if (requestIdRef.current !== currentRequestId) {
        return null;
      }

      setState({
        data: null,
        loading: false,
        error,
      });
      return null;
    }
  }, [editionNumber, productId, resetState, shouldLoad]);

  useEffect(() => {
    fetchCopy();
  }, [fetchCopy]);

  return useMemo(() => ({
    data: state.data,
    loading: state.loading,
    error: state.error,
    refresh: fetchCopy,
    hasData: Boolean(state.data),
  }), [fetchCopy, state.data, state.error, state.loading]);
}
